/**
 * SiteList — 공사현황 현장 목록 (플랫폼 apps/shell-web/SiteList.tsx 구조·클래스 복제)
 *
 * 플랫폼과 동일한 UI: 키워드 검색+검색버튼 · 날짜 필터(토글) · 상태 필터(토글) · 현장 카드.
 * 데이터만 공사현황(worklog)으로 바인딩 → GET /api/worklog/worklogs
 *   - 상태: E01_SCHEDULE_STATUS (준비중/시공중/시공완료 3종 + 전체)
 *   - 카드: 공사기간(constStartDate ~ constEndDate)
 * 화면오픈 오케스트레이션: 첫 조회 완료 시 onFirstLoad() 1회 호출.
 * 현장 등록 완료 시 reloadTick 증가로 재조회.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { StatusChip } from '../StatusChip';
import { Badge, Button, Modal, SearchCombo } from '@indipia/ui-core';
import { getSiteColor } from '@indipia/shared';
import {
  MonthCalendar, shiftMonth, parseYm, fetchHolidays,
  AssigneeBadges, AssigneeSelect, useCompanyMembers, useDefaultMemberFilter, useMemberFilterState,
} from '@indipia/shared-features';
import { worklogApi } from '../../api/worklogApi';
import { useWorklogStore } from '../../store/worklogStore';
import { useScheduleStatusOptions } from '../../utils/scheduleStatus';
import type { WorklogDeleteInfo, WorklogListItem } from '../../types/worklog.types';

const PAGE_SIZE = 20;

// ── 상수 ────────────────────────────────────────────────────────────────────

type DateMode = 'direct' | 1 | 3 | 6 | 12;

const DATE_MODES: Array<{ label: string; mode: DateMode }> = [
  { label: '직접 설정', mode: 'direct' },
  { label: '1개월',    mode: 1 },
  { label: '3개월',    mode: 3 },
  { label: '6개월',    mode: 6 },
  { label: '1년',      mode: 12 },
];

// ── 헬퍼 ────────────────────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, '0');
const toDash = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
/** 'YYYY-MM-DD' → 'MM.DD' (카드 공사기간 표시용) */
function toMMDD(isoDate: string): string {
  const [, m, d] = isoDate.split('-');
  return m && d ? `${m}.${d}` : isoDate;
}
function monthsAgo(n: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return toDash(d);
}

/** 완공(종료일) 기준 D-day — 오늘=D-day, 남으면 D-{n}, 지났으면 D+{n} */
function ddayLabel(endDate?: string | null): string {
  if (!endDate) return '';
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(end.getTime())) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((end.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return 'D-day';
  return diff > 0 ? `D-${diff}` : `D+${-diff}`;
}

/**
 * 공사기간 인라인 수정 패널 — 현장카드 톱니바퀴 클릭 시 카드 안에 펼쳐진다.
 * 좁은 카드 폭에 맞춰 달력 1개로 시작일→종료일을 순서대로 선택한다(StepD의 2-달력 레이아웃과 다름).
 * 달력·기간 요약 박스·저장 흐름은 StepD와 같은 MonthCalendar를 그대로 재사용한다.
 */
function PeriodEditPanel({
  initialStart,
  initialEnd,
  saving,
  onCancel,
  onSave,
}: {
  initialStart: string | null;
  initialEnd: string | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (start: string, end: string) => void;
}) {
  const today = new Date();
  const thisYm = { year: today.getFullYear(), month: today.getMonth() + 1 };
  const [start, setStart] = useState(initialStart ?? '');
  const [end, setEnd] = useState(initialEnd ?? '');
  const [ym, setYm] = useState(() => parseYm(initialStart ?? '') ?? thisYm);

  const holRange = useMemo(() => ({ from: `${ym.year}-01-01`, to: `${ym.year}-12-31` }), [ym.year]);
  const [holidays, setHolidays] = useState<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    fetchHolidays(holRange.from, holRange.to).then((dates) => {
      if (!cancelled) setHolidays(new Set(dates));
    });
    return () => {
      cancelled = true;
    };
  }, [holRange.from, holRange.to]);

  // 시작일 → 종료일 순서로 한 달력에서 선택. 둘 다 이미 있으면 새로 고른 날짜부터 다시 시작한다.
  function handlePick(iso: string) {
    if (!start || (start && end)) {
      setStart(iso);
      setEnd('');
    } else if (iso < start) {
      setStart(iso);
    } else {
      setEnd(iso);
    }
  }

  const periodLabel = start && end
    ? `${start} ~ ${end}`
    : start
      ? `${start} ~ 종료일을 선택하세요`
      : '시작일을 선택해주세요';

  return (
    <div
      className="mt-2 rounded-lg border border-border bg-surface p-2.5"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="flex mt-1 text-caption text-text-muted">시작일 → 종료일을 순서대로 선택하세요.</p>

      <div className="flex mt-2 items-center gap-2 rounded-md border border-border bg-surface-raised px-2.5 py-2 text-caption text-text">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="shrink-0 text-text-muted">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span className="flex-1">{periodLabel}</span>
      </div>

      <div className="mt-2">
        <MonthCalendar
          ym={ym}
          selected={start}
          rangeStart={start}
          rangeEnd={end}
          onNav={(d) => setYm((y) => shiftMonth(y, d))}
          onPick={handlePick}
          holidays={holidays}
        />
      </div>

      <div className="mt-2.5 flex items-center justify-end gap-2 border-t border-border pt-2.5">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          취소
        </Button>
        <Button size="sm" disabled={!start || !end || saving} onClick={() => onSave(start, end)}>
          {saving ? '저장 중…' : '저장'}
        </Button>
      </div>
    </div>
  );
}

// 시공완료/삭제 확인 다이얼로그 — 인디스케줄(SiteListView)과 톤·아이콘 통일
function ConfirmDialog({
  tone, title, message, confirmLabel, busy, onCancel, onConfirm,
}: {
  tone: 'success' | 'error';
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal open onClose={onCancel}>
      {/* 헤더 — 배경색 없음. 아이콘은 인디피아 메인색(point), 텍스트는 중립 */}
      {/* title 없는 Modal이라 .modal-head가 렌더되지 않아 .modal-body 상단 패딩(4px)만 남는다 — 하단(14px)과 균형 맞춰 pt-2.5(10px) 보강 */}
      <div className="flex items-start gap-3 pt-2.5">
        <span className="shrink-0 text-group">
          {tone === 'success'
            ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>}
        </span>
        <div>
          <div className="mb-1 text-heading-sm font-bold text-text-primary">{title}</div>
          <div className="text-body-sm leading-relaxed text-text-secondary">{message}</div>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="primary" size="sm" onClick={onCancel}>취소</Button>
        <Button
          variant={tone === 'error' ? 'etc' : 'primary'}
          size="sm"
          disabled={busy}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

// ── 하위 컴포넌트 (플랫폼 동일) ───────────────────────────────────────────────

function FilterPill({
  label, active, onClick, darkActive = false,
}: {
  label: string; active: boolean; onClick: () => void; darkActive?: boolean;
}) {
  const activeCls = darkActive
    ? 'border-text bg-text text-surface-raised'
    : 'border-group bg-group text-group-onPrimary';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${
        active ? activeCls : 'border-border text-text-secondary hover:border-group hover:text-group'
      }`}
    >
      {label}
    </button>
  );
}

function SectionToggle({
  label, open, onToggle, summary,
}: {
  /** 접힌 상태 요약 — 배열이면 항목마다 배지 하나(예: 담당자·관리자 섹션의 선택 직원 이름) */
  label: string; open: boolean; onToggle: () => void; summary?: string | string[];
}) {
  const summaries = summary == null ? [] : Array.isArray(summary) ? summary : [summary];
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full shrink-0 items-center justify-between border-b border-border px-2.5 py-2 text-left transition-colors hover:bg-surface"
    >
      <span className="flex min-w-0 flex-wrap items-center gap-02">
        <span className="text-caption font-semibold text-text-secondary">{label}</span>
        {summaries.map((s, i) => (
          <Badge key={`${i}-${s}`} status="accent" className="!rounded-full">
            {s}
          </Badge>
        ))}
      </span>
      <svg
        width="11" height="11" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        className={`shrink-0 text-text-muted transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );
}

// 날짜 필드 — 표시는 yyyy-mm-dd 고정(로캘 무관), 클릭 시 네이티브 달력(showPicker)
function DateField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div
      onClick={() => ref.current?.showPicker?.()}
      className="relative flex min-w-0 flex-1 cursor-pointer items-center rounded-sm border border-border px-1.5 py-1 text-xs text-text-secondary"
    >
      <span className="truncate">{value || 'YYYY-MM-DD'}</span>
      <input
        ref={ref}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
        aria-label="날짜 선택"
      />
    </div>
  );
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────

export function SiteList({
  reloadTick = 0,
  pinSiteId = null,
  initialStatus = '',
  onFirstLoad,
  isSelected,
  onToggle,
  onSitesLoaded,
  onDeleted,
}: {
  reloadTick?: number;
  /** 방금 등록한 현장 — 목록 맨 앞에 끼워 넣는다(정렬·페이지·조건과 무관) */
  pinSiteId?: number | null;
  /** 마운트 시 기본 선택할 상태 칩 코드 (''=전체). '진행중인 현장' 진입시 'IN_PROGRESS'. */
  initialStatus?: string;
  onFirstLoad?: () => void;
  /** 현장 선택 여부 (해제되면 false) — 미지정 시 항상 선택으로 간주 */
  isSelected?: (siteId: number) => boolean;
  /**
   * 현장 카드 클릭 → 선택/해제 토글.
   * 2번째 인자로 클릭된 현장 항목을 함께 넘긴다 — basic-info(시공중만)에 없는 현장
   * (준비중·시공완료)을 선택한 화면이 목록 데이터로 상세를 그릴 수 있게 하기 위함.
   */
  onToggle?: (siteId: number, item: WorklogListItem) => void;
  /** 조회 완료 시 현재 목록의 전체 siteId 통보 — 페이지의 단일 선택(나머지 해제) 계산용 */
  /** 2번째 인자 = 조회된 현장 원본(진행상태 등) — 화면이 '시공중만 활성화' 같은 초기 선택에 쓴다 */
  onSitesLoaded?: (siteIds: number[], items: WorklogListItem[]) => void;
  /**
   * 현장 삭제 완료 시 통보(siteId, worklogId) — 페이지의 본문(작업요청·현장보고·통합피드 등)이
   * 삭제된 현장의 데이터를 여전히 들고 있으므로, 이 콜백을 받아 각자 재조회해야 반영된다.
   * SiteList 자신의 목록은 로컬에서 바로 제거하므로(관여 불필요) 여기선 통보만 한다.
   */
  onDeleted?: (siteId: number, worklogId: number) => void;
}) {
  // 검색
  const [searchInput, setSearchInput] = useState('');
  const [keyword,     setKeyword]     = useState('');

  // 날짜 섹션 (기본 최근 3개월)
  const [dateOpen, setDateOpen] = useState(false);
  const [dateMode, setDateMode] = useState<DateMode | ''>(''); //useState<DateMode>(3);
  const [dateFrom, setDateFrom] = useState(''); //useState(() => monthsAgo(3));
  const [dateTo,   setDateTo]   = useState(''); //useState(() => toDash(new Date()));

  // 상태 섹션 — 다중 선택 집합(빈 집합 = 전체). 진입 경로에 따라 초기값('진행 중인 현장' 진입 시 시공중).
  const [statusOpen,   setStatusOpen]   = useState(false);
  const [statusFilter, setStatusFilter] = useState<Set<string>>(
    () => (initialStatus ? new Set([initialStatus]) : new Set()),
  );

  // 담당자·관리자 섹션(2026-10-12 개선사항 D-9 · D-12~D-14) — 기본값: 직원은 나, 사장님은 조건 없음(전체 현장).
  // 서버(W-2 memberIds)가 페이징 전에 거르므로 현장 건수도 이 조건을 반영한다.
  const { myId } = useDefaultMemberFilter();
  const { data: companyMembers = [] } = useCompanyMembers();
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberIds,  setMemberIds]  = useMemberFilterState();
  const memberIdsKey = memberIds.join(',');
  // 접힌 상태 요약 — 선택 직원마다 배지 하나(날짜·상태 요약과 같은 배지)
  const memberSummary = memberIds.map(
    (id) => companyMembers.find((m) => m.memberId === id)?.memberName ?? (id === myId ? '나' : `회원 ${id}`),
  );

  // 데이터
  const [items, setItems]     = useState<WorklogListItem[]>([]);
  // 전체 현장 수 — 상태 필터와 무관한 서버 totalCount(누적 완료분 포함, 20건 초과해도 정확). 삭제 시만 감소.
  const [total, setTotal]     = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [isError, setError]   = useState(false);
  const firstLoadDone = useRef(false);

  // 카드 액션(시공완료/삭제) — 확인 다이얼로그 대상 + 처리 중 worklogId
  const [completeTarget, setCompleteTarget] = useState<WorklogListItem | null>(null);
  const [deleteTarget,   setDeleteTarget]   = useState<WorklogListItem | null>(null);
  const [busyId,         setBusyId]         = useState<number | null>(null);

  // 공사기간 인라인 수정 — 톱니바퀴 클릭 시 그 카드만 펼친다(한 번에 하나만 열림).
  const [editingPeriodId, setEditingPeriodId] = useState<number | null>(null);
  const [periodSaving,    setPeriodSaving]    = useState(false);

  // 삭제 확인창에 노출할 하위 데이터 건수(작업요청·현장보고) — deleteTarget 지정 시 조회
  const [deleteInfo, setDeleteInfo] = useState<WorklogDeleteInfo | null>(null);
  const [deleteInfoLoading, setDeleteInfoLoading] = useState(false);

  useEffect(() => {
    if (!deleteTarget) {
      setDeleteInfo(null);
      return;
    }
    const controller = new AbortController();
    setDeleteInfo(null);
    setDeleteInfoLoading(true);
    worklogApi.getWorklogDeleteInfo(deleteTarget.worklogId, controller.signal)
      .then((res) => setDeleteInfo(res.data?.data ?? null))
      .catch(() => setDeleteInfo(null))
      .finally(() => setDeleteInfoLoading(false));
    return () => controller.abort();
  }, [deleteTarget]);

  /**
   * 시공완료 대상의 **인디플래너 스케줄 연동 여부** — 확인 다이얼로그 안내문 분기용.
   *
   * 목록 API(§1.1)는 `scheduleId`를 주지 않는다. `progressRate`(스케줄 없으면 null)로 대신
   * 판별했더니 스케줄이 연결돼 있어도 진척율이 null로 내려오는 경우가 있어 안내가 누락됐다.
   * → basic-info(§5.3)는 `scheduleId`를 주므로 그쪽을 1순위로 조회하고, 거기서 못 찾은
   *   현장만 `progressRate`로 보조 판별한다.
   */
  const storeWorklogs = useWorklogStore((s) => s.worklogs);
  const completeHasSchedule = useMemo(() => {
    if (!completeTarget) return false;
    const fromStore = storeWorklogs.find((w) => w.worklogId === completeTarget.worklogId);
    if (fromStore) return fromStore.scheduleId != null;
    return completeTarget.progressRate != null; // basic-info 미포함 현장 보조 판별
  }, [completeTarget, storeWorklogs]);

  // E01_SCHEDULE_STATUS 공통코드 — 라벨·선택지 모두 utils/scheduleStatus 경유(폴백 포함).
  // 현장 목록 필터는 '지연(DELAYED)'을 노출하지 않는다.
  const statusCodes = useScheduleStatusOptions(['DELAYED']);

  // 조회 — 키워드/날짜/상태/reloadTick 변경 시
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    worklogApi
      .getWorklogs(
        {
          // 진행상태 필터는 다중 선택이라 서버 단일 status로 표현 불가 → 전체 조회 후 클라이언트에서 필터.
          // 정렬은 서버가 고정 적용(진행상태 우선순위 → 공사시작일 최신순)하므로 sort 파라미터를 넘기지 않는다.
          keyword: keyword || undefined,
          constStartDateFrom: dateFrom || undefined,
          constStartDateTo: dateTo || undefined,
          memberIds: memberIds.length ? memberIds : undefined,
          size: PAGE_SIZE,
        },
        controller.signal,
      )
      .then(async (res) => {
        let list = res.data.data.items;
        // 새로 등록한 현장은 맨 앞 — 이번 페이지·조건에 없으면 그 현장만 따로 받아 끼워 넣는다
        if (pinSiteId != null) {
          let pinned = list.find((it) => it.siteId === pinSiteId);
          if (!pinned) {
            const one = await worklogApi.getWorklogs({ siteId: [pinSiteId], size: 1 }, controller.signal).catch(() => null);
            pinned = one?.data.data.items[0];
          }
          if (pinned) list = [pinned, ...list.filter((it) => it.siteId !== pinSiteId)];
        }
        if (controller.signal.aborted) return;
        setItems(list);
        setTotal(res.data.data.totalCount);
        onSitesLoaded?.(list.map((it) => it.siteId), list);
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setError(true);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        if (!firstLoadDone.current) {
          firstLoadDone.current = true;
          onFirstLoad?.();
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, dateFrom, dateTo, reloadTick, memberIdsKey]);

  function handleDateModeChange(mode: DateMode) {
    // 토글: 이미 활성인 칩을 다시 누르면 해제 → 날짜 조건을 비우고 전체조회 상태로 되돌린다.
    if (mode === dateMode) {
      setDateMode('');
      setDateFrom('');
      setDateTo('');
      return;
    }
    setDateMode(mode);
    if (mode !== 'direct') {
      setDateFrom(monthsAgo(mode));
      setDateTo(toDash(new Date()));
    }
  }

  // 상태칩 토글(다중 선택). 토글 결과 모든 상태가 선택되면 집합을 비워 '전체'로 수렴.
  function toggleStatus(code: string) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      if (next.size === statusCodes.length) next.clear();
      return next;
    });
  }

  // 시공완료 처리 — PATCH progressStatus=DONE, 목록은 로컬 반영.
  // syncSchedule=true 로 보내야 BE가 연동된 인디플래너 공사스케줄까지 함께 종료 처리한다.
  async function handleComplete() {
    if (!completeTarget) return;
    const w = completeTarget;
    setCompleteTarget(null);
    setBusyId(w.worklogId);
    try {
      const res = await worklogApi.updateWorklogStatus(w.worklogId, 'DONE', { syncSchedule: true });
      setItems((prev) => prev.map((it) => (it.worklogId === w.worklogId ? { ...it, progressStatus: 'DONE' } : it)));
      // BE는 정상 완료에도 message를 담아 주므로(예: "공사현장이 시공완료되었습니다")
      // message 유무로는 부분 실패를 구분할 수 없다 → code 로 판별한다.
      // 부분 실패 = 공사현황은 완료됐으나 인디플래너 스케줄 종료 실패(HTTP 200, success=true).
      // 사용자가 인디플래너에서 직접 확인해야 하는 안내라 경고 톤 + 긴 노출시간.
      // TODO(BE 확정): 부분 실패 code 값이 정해지면 아래 판정을 그 값 비교로 바꾼다.
      const { code, message } = res.data ?? {};
      const msg = message?.trim();
      if (code && code !== 'OK') toast.warning(msg || '스케줄 종료 처리를 확인해 주세요.', { duration: 8000 });
      else toast.success(msg || '시공완료 처리되었습니다.');
    } catch {
      toast.error('시공완료 처리에 실패했습니다.');
    } finally {
      setBusyId(null);
    }
  }

  // 공사기간 수정 — PATCH constStartDate/constEndDate, 목록은 로컬 반영(API명세_20260721 §1.4)
  async function handleSavePeriod(worklogId: number, start: string, end: string) {
    setPeriodSaving(true);
    try {
      await worklogApi.updateWorklogPeriod(worklogId, { constStartDate: start, constEndDate: end });
      setItems((prev) =>
        prev.map((it) => (it.worklogId === worklogId ? { ...it, constStartDate: start, constEndDate: end } : it)),
      );
      setEditingPeriodId(null);
      toast.success('공사기간이 변경되었습니다.');
    } catch {
      toast.error('공사기간 변경에 실패했습니다.');
    } finally {
      setPeriodSaving(false);
    }
  }

  // 공사현황 삭제 — DELETE, 목록에서 제거
  async function handleDelete() {
    if (!deleteTarget) return;
    const w = deleteTarget;
    setDeleteTarget(null);
    setBusyId(w.worklogId);
    try {
      await worklogApi.deleteWorklog(w.worklogId);
      setItems((prev) => prev.filter((it) => it.worklogId !== w.worklogId));
      setTotal((t) => Math.max(0, t - 1)); // 삭제 시 전체 현장 수 감소(시공완료는 그대로)
      // basic-info 캐시(worklogStore) 무효화 — 작업요청 작성 등 다른 화면의 현장 셀렉박스가
      // 삭제된 현장을 계속 보여주는 것을 방지. 이 목록(SiteList)의 items와는 별개 캐시라
      // 갱신 안 하면 삭제 후에도 새로고침 전까지 그 화면들에 남아 보인다.
      void useWorklogStore.getState().refresh();
      onDeleted?.(w.siteId, w.worklogId); // 페이지 본문(작업요청·현장보고 등) 재조회 위임
      toast.success('공사현장이 삭제되었습니다.');
    } catch {
      toast.error('삭제에 실패했습니다.');
    } finally {
      setBusyId(null);
    }
  }

  // 섹션 토글 헤더 요약
  const dateSummary = (() => {
    if (dateMode !== 'direct') return DATE_MODES.find((m) => m.mode === dateMode)?.label;
    if (dateFrom && dateTo) return `${dateFrom} ~ ${dateTo}`;
    if (dateFrom) return `${dateFrom} ~`;
    return undefined;
  })();

  const statusSummary = (() => {
    if (statusFilter.size === 0) return undefined; // 전체 — 요약 뱃지 없음
    // 선택된 상태를 모두 나열(코드 정렬 순). 3개 다 선택 시 '전체'로 수렴하므로 최대 2개만 표시된다.
    return statusCodes
      .filter((c) => statusFilter.has(c.code))
      .map((c) => c.codeName)
      .join(', ');
  })();

  // 화면 표시 목록 — 선택 상태(다중)로 필터만 적용. 정렬(진행상태 우선순위 → 공사시작일 최신순)은
  // BE(GET /worklog/worklogs, WorklogRepository#searchFilter)가 고정 적용해 내려주므로 순서를 그대로 유지한다.
  const visibleItems = items.filter(
    (it) => statusFilter.size === 0 || statusFilter.has(it.progressStatus),
  );

  return (
    <div className="flex flex-1 flex-col overflow-hidden">

      {/* ① 키워드 검색 */}
      <div className="shrink-0 border-b border-border px-2.5 py-2">
        <SearchCombo
          value={searchInput}
          onChange={setSearchInput}
          onSearch={(v) => setKeyword(v.trim())}
          onBlur={() => setKeyword(searchInput.trim())}
          placeholder="현장명, 주소, 고객명"
        />
      </div>

      {/* ② 날짜 필터 (토글) */}
      <SectionToggle
        label="날짜"
        open={dateOpen}
        onToggle={() => setDateOpen((v) => !v)}
        summary={dateOpen ? undefined : dateSummary}
      />
      {dateOpen && (
        <div className="shrink-0 space-y-2 border-b border-border bg-surface px-2.5 py-2">
          {/* 기간 단축 pills */}
          <div className="flex gap-1 overflow-x-auto">
            {DATE_MODES.map(({ label, mode }) => (
              <FilterPill
                key={mode}
                label={label}
                active={dateMode === mode}
                onClick={() => handleDateModeChange(mode)}
                darkActive
              />
            ))}
          </div>
          {/* 날짜 범위 (yyyy-mm-dd) */}
          <div className="flex items-center gap-1.5">
            <DateField value={dateFrom} onChange={(v) => { setDateMode('direct'); setDateFrom(v); }} />
            <span className="shrink-0 text-xs text-text-muted">~</span>
            <DateField value={dateTo} onChange={(v) => { setDateMode('direct'); setDateTo(v); }} />
          </div>
        </div>
      )}

      {/* ③ 상태 필터 (토글) */}
      <SectionToggle
        label="상태"
        open={statusOpen}
        onToggle={() => setStatusOpen((v) => !v)}
        summary={statusOpen ? undefined : statusSummary}
      />
      {statusOpen && (
        <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-border bg-surface px-2.5 py-2">
          <FilterPill
            label="전체"
            active={statusFilter.size === 0}
            onClick={() => setStatusFilter(new Set())}
            darkActive
          />
          {statusCodes.map((code) => (
            <FilterPill
              key={code.code}
              label={code.codeName}
              active={statusFilter.has(code.code)}
              onClick={() => toggleStatus(code.code)}
              darkActive
            />
          ))}
        </div>
      )}

      {/* ④ 담당자·관리자 필터 (토글) — D-9. 접힌 상태에서는 선택 직원 이름을 배지로 표시 */}
      <SectionToggle
        label="담당자·관리자"
        open={memberOpen}
        onToggle={() => setMemberOpen((v) => !v)}
        summary={memberOpen ? undefined : memberSummary}
      />
      {memberOpen && (
        <div className="shrink-0 border-b border-border bg-surface px-2.5 py-03">
          <AssigneeSelect
            members={companyMembers}
            values={memberIds}
            onChange={setMemberIds}
            myId={myId}
          />
        </div>
      )}

      {/* ⑤ 현장 카드 목록 */}
      <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-2.5 py-2">
        <p className="px-0.5 text-caption">
          <b className="text-text-secondary">{visibleItems.length}</b>
          <span className="text-text-muted"> / {total} 건</span>
        </p>
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center py-10">
            <p className="text-caption text-text-muted">불러오는 중…</p>
          </div>
        ) : isError ? (
          <div className="flex flex-1 items-center justify-center py-10">
            <p className="text-caption text-text-muted">현장 목록을 불러오지 못했습니다</p>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-10">
            <p className="text-caption text-text-muted">조건에 맞는 현장이 없습니다</p>
          </div>
        ) : (
          visibleItems.map((item) => {
            const selected = isSelected ? isSelected(item.siteId) : true;
            return (
              <div
                key={item.worklogId}
                onClick={() => onToggle?.(item.siteId, item)}
                data-selected={selected}
                className={[
                  'flex cursor-pointer rounded-md border border-border transition-all',
                  selected
                    ? 'bg-surface-raised hover:border-group'
                    : 'bg-surface-muted', // opacity 감쇠는 텍스트 대비(AA) 미달이라 제거 — 해제 표시는 grayscale·muted 배경·회색 바
                ].join(' ')}
              >
                {/* 현장 색상 바 — 선택 시 팔렛트 색, 해제 시 그레이 */}
                <div
                  className={`w-[5px] shrink-0 self-stretch rounded-tl-md rounded-bl-md ${selected ? '' : 'bg-border'}`}
                  style={selected ? { background: getSiteColor(item.siteId) } : undefined}
                />

                {/* 카드 내용 — 해제 시 grayscale 은 2행부터만 건다. 1행 시공상태칩은 상태 색을 유지해야 해서 제외(필터는 자식에서 되돌릴 수 없다) */}
                <div className={`min-w-0 flex-1 px-3 py-2.5 ${selected ? '' : '[&>*:not(:first-child)]:grayscale'}`}>
                  {/* 1행: 현장명 + 시공상태칩 */}
                  <div className="mb-1 flex items-center justify-between gap-1">
                    <span className="truncate text-body-sm font-bold text-text">
                      {item.siteName}
                    </span>
                    <StatusChip code={item.progressStatus} />
                  </div>

                  {/* 2행: 고객명 + 시공완료·삭제 액션(2열 중 1행 — 톱니는 3행으로 이동) */}
                  <div className="mb-1 flex items-center justify-between gap-1">
                    <p className="truncate text-caption leading-snug text-text-secondary">
                      {item.customerName}
                    </p>
                    <div className="flex shrink-0 items-center gap-0.5">
                      {item.progressStatus !== 'DONE' && (
                        <button
                          type="button"
                          title="시공완료 처리"
                          aria-label="시공완료 처리"
                          disabled={busyId === item.worklogId}
                          onClick={(e) => { e.stopPropagation(); setCompleteTarget(item); }}
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-text-muted transition-colors hover:text-group disabled:opacity-40"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                        </button>
                      )}
                      <button
                        type="button"
                        title="공사현황에서 삭제"
                        aria-label="삭제"
                        disabled={busyId === item.worklogId}
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-text-muted transition-colors hover:text-error disabled:opacity-40"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                      </button>
                    </div>
                  </div>

                  {/* 3행: 공사기간 + D-day(완공 기준) + 공사기간 수정(톱니, 2열 중 2행).
                      2행에 체크 아이콘이 있으면(시공중 등) 톱니 오른쪽에 트래시 폭만큼 spacer를 둬서
                      톱니가 체크 아이콘 바로 아래 열에 오도록 맞춘다. */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="text-xs text-text-muted">
                        {item.constStartDate && item.constEndDate
                          ? `${toMMDD(item.constStartDate)} ~ ${toMMDD(item.constEndDate)}`
                          : '공사기간 미정'}
                      </span>
                      {item.constEndDate && item.progressStatus !== 'DONE' && (
                        <span className="shrink-0 text-xs font-bold text-group">
                          {ddayLabel(item.constEndDate)}
                        </span>
                      )}
                    </div>
                    {item.progressStatus !== 'DONE' && (
                      <div className="flex shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          title="공사기간 수정"
                          aria-label="공사기간 수정"
                          disabled={busyId === item.worklogId}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPeriodId((cur) => (cur === item.worklogId ? null : item.worklogId));
                          }}
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-sm transition-colors hover:text-group disabled:opacity-40 ${
                            editingPeriodId === item.worklogId ? 'text-group' : 'text-text-muted'
                          }`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                        </button>
                        <span className="h-6 w-6 shrink-0" aria-hidden="true" />
                      </div>
                    )}
                  </div>

                  {/* 공사기간 인라인 수정 — 톱니바퀴 클릭 시 카드 안에 펼쳐진다 */}
                  {editingPeriodId === item.worklogId && (
                    <PeriodEditPanel
                      initialStart={item.constStartDate}
                      initialEnd={item.constEndDate}
                      saving={periodSaving}
                      onCancel={() => setEditingPeriodId(null)}
                      onSave={(start, end) => handleSavePeriod(item.worklogId, start, end)}
                    />
                  )}

                  {/* 담당자 · 관리자 — 공사기간 줄 아래(캘린더를 열면 그 사이에 캘린더). 지정 없으면 '미지정' (D-9) */}
                  <AssigneeBadges
                    chargeMembers={item.chargeMembers}
                    managerMembers={item.managerMembers}
                    filterIds={memberIds}
                    myId={myId}
                    emptyText="미지정"
                    popoverMe
                    className="mt-03"
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 시공완료 확인 */}
      {completeTarget && (
        <ConfirmDialog
          tone="success"
          title="시공완료 처리"
          message={
            <>
              <strong>"{completeTarget.siteName}"</strong> 현장을 시공완료 상태로 변경합니다.
              <br />
              {/* 스케줄 연동 시에만 노출 — 판별 근거는 completeHasSchedule 주석 참조 */}
              {completeHasSchedule && (
                <>
                  <span className="text-text">
                    인디플래너의 <strong>스케줄도 함께 완료</strong>되며,
                  </span>
                  <br />
                </>
              )}
              완료 처리 후에는 되돌릴 수 없습니다.
            </>
          }
          confirmLabel="시공완료 처리"
          busy={busyId === completeTarget.worklogId}
          onCancel={() => setCompleteTarget(null)}
          onConfirm={handleComplete}
        />
      )}

      {/* 삭제 확인 — 하위 데이터(작업요청·현장보고) 건수를 조회해 안내에 포함한다. 둘 다/하나만/둘 다 없음 모두 자연스럽게 조합 */}
      {deleteTarget && (() => {
        const parts: string[] = [];
        if (deleteInfo && deleteInfo.workOrderCount > 0) parts.push(`작업요청 ${deleteInfo.workOrderCount}건`);
        if (deleteInfo && deleteInfo.fieldReportCount > 0) parts.push(`현장보고 ${deleteInfo.fieldReportCount}건`);
        const hasData = parts.length > 0;
        return (
          <ConfirmDialog
            tone="error"
            title="공사현장 삭제"
            message={
              <>
                {deleteInfoLoading ? (
                  <>
                    <strong>"{deleteTarget.siteName}"</strong> 현장을 삭제합니다.
                    <br />
                    연결된 데이터를 확인하는 중입니다...
                  </>
                ) : hasData ? (
                  <>
                    <strong>"{deleteTarget.siteName}"</strong> 현장에{' '}<br />
                    <strong>{parts.join(', ')}</strong>이 있습니다.
                    <br />
                    그래도 삭제하시겠습니까?
                    <br />
                    삭제된 데이터는 <strong>복구할 수 없습니다.</strong>
                  </>
                ) : (
                  <>
                    <strong>"{deleteTarget.siteName}"</strong> 현장을 삭제합니다.
                    <br />
                    삭제된 데이터는 <strong>복구할 수 없습니다.</strong>
                  </>
                )}
              </>
            }
            confirmLabel="삭제"
            busy={busyId === deleteTarget.worklogId || deleteInfoLoading}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={handleDelete}
          />
        );
      })()}
    </div>
  );
}
